import React, { useState, useEffect } from 'react';
import { createProjectRole, updateProjectRole, deleteProjectRole } from '../api/roles';
import { getAllUsers } from '../api/users';
import { ProjectRole } from '../types/role';
import { User } from '../types/user';
import Button from './Button';

interface ProjectRoleFormProps {
  projectId: string;
  role?: ProjectRole;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ProjectRoleForm: React.FC<ProjectRoleFormProps> = ({ projectId, role, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<{
    user_id: string;
    role: 'Editor' | 'Viewer' | '';
  }>({
    user_id: '',
    role: '',
  });
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditMode = !!role;

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (role) {
      setFormData({
        user_id: role.user_id,
        role: role.role === 'Editor' ? 'Editor' : 'Viewer',
      });
    }
  }, [role]);

  const loadUsers = async () => {
    try {
      const allUsers = await getAllUsers();
      setUsers(allUsers);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isEditMode && role) {
        await updateProjectRole(role.id, { role: formData.role as 'Editor' | 'Viewer' });
      } else {
        await createProjectRole({
          project_id: projectId,
          user_id: formData.user_id,
          role: formData.role as 'Editor' | 'Viewer',
        });
      }
      
      setFormData({ user_id: '', role: '' });
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'assign'} role`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!role || !window.confirm('Are you sure you want to remove this role assignment?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteProjectRole(role.id);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete role';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card">
      <h3 style={{ marginTop: 0, marginBottom: 'var(--spacing-lg)', color: 'var(--gray-900)' }}>
        {isEditMode ? 'Edit Role Assignment' : 'Assign Team Member Role'}
      </h3>
      
      {error && <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100">{error}</div>}
      
      {!isEditMode && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Team Member <span style={{ color: 'var(--error)' }}>*</span>
          </label>
          <select
            name="user_id"
            value={formData.user_id}
            onChange={handleChange}
            required
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="">-- Select Team Member --</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.full_name || user.username} ({user.email})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Access Level <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <select
          name="role"
          value={formData.role}
          onChange={handleChange}
          required
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
        >
          <option value="">-- Select Access Level --</option>
          <option value="Editor">Editor (can create, edit, and delete)</option>
          <option value="Viewer">Viewer (read-only)</option>
        </select>
        <small style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
          Editors can manage this project; Viewers have read-only access.
        </small>
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-lg)' }}>
        <Button type="submit" disabled={loading} variant="primary">
          {loading ? (isEditMode ? 'Updating...' : 'Assigning...') : (isEditMode ? 'Update Role' : 'Assign Role')}
        </Button>
        {isEditMode && (
          <Button type="button" onClick={handleDelete} disabled={loading} variant="danger">
            Remove Role
          </Button>
        )}
        {onCancel && (
          <Button type="button" onClick={onCancel} variant="outline">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default ProjectRoleForm;
